import { getToken } from "./tokenProvider";

const MAX_QUEUE_SIZE = 100;
const MAX_EVENT_MESSAGE_LENGTH = 4000;
const FLUSH_DELAY_MS = 1000;
const RETRY_DELAY_MS = 10000;
const SENSITIVE_KEY = /authorization|cookie|password|secret|token|xml|raw.?order/i;

let pendingEvents = [];
let flushTimer = null;
let sending = false;

function redactText(value) {
  const text = String(value)
    .replace(/bearer\s+[a-z0-9._-]+/gi, "Bearer [redacted]")
    .replace(/([?&](?:token|password|secret)=)[^&\s]+/gi, "$1[redacted]");

  return /<\/?[a-z][^>]*>/i.test(text) ? "[XML/HTML content redacted]" : text;
}

function serializeValue(value) {
  if (value instanceof Error) {
    return `${value.name}: ${redactText(value.message)}`;
  }

  if (typeof value === "string") return redactText(value);
  if (value == null || typeof value !== "object") return redactText(String(value));

  const seen = new WeakSet();
  try {
    return JSON.stringify(value, (key, nestedValue) => {
      if (SENSITIVE_KEY.test(key)) return "[redacted]";
      if (typeof nestedValue === "object" && nestedValue !== null) {
        if (seen.has(nestedValue)) return "[circular]";
        seen.add(nestedValue);
      }
      return nestedValue;
    });
  } catch {
    return "[unserializable value]";
  }
}

function queueEvent(event) {
  pendingEvents.push(event);
  if (pendingEvents.length > MAX_QUEUE_SIZE) pendingEvents.shift();
  scheduleFlush();
}

function scheduleFlush(delay = FLUSH_DELAY_MS) {
  if (flushTimer || sending) return;
  flushTimer = window.setTimeout(() => {
    flushTimer = null;
    flushClientLogs();
  }, delay);
}

async function flushClientLogs() {
  if (sending || pendingEvents.length === 0) return;

  const token = await getToken();
  if (!token) {
    scheduleFlush(RETRY_DELAY_MS);
    return;
  }

  sending = true;
  const events = pendingEvents.splice(0, 25);
  let retryDelay = FLUSH_DELAY_MS;
  try {
    const response = await fetch("/api/client-logs", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ events }),
      keepalive: true,
    });

    if (!response.ok) throw new Error(`Client log request failed: ${response.status}`);
  } catch {
    pendingEvents = [...events, ...pendingEvents].slice(-MAX_QUEUE_SIZE);
    retryDelay = RETRY_DELAY_MS;
  } finally {
    sending = false;
    if (pendingEvents.length > 0) scheduleFlush(retryDelay);
  }
}

function createEvent(level, args, source = "console") {
  const error = args.find((value) => value instanceof Error);
  const message = args
    .map(serializeValue)
    .join(" ")
    .slice(0, MAX_EVENT_MESSAGE_LENGTH);

  return {
    timestamp: new Date().toISOString(),
    level,
    message: message || "[empty console event]",
    stack: error?.stack ? redactText(error.stack) : null,
    url: `${window.location.origin}${window.location.pathname}`,
    userAgent: navigator.userAgent,
    source,
  };
}

// Use this for state-changing KDS actions. It stays separate from noisy console
// output and is stored as structured JSON in the daily frontend log.
export function logClientActivity(activity, context = {}) {
  queueEvent({
    timestamp: new Date().toISOString(),
    level: "info",
    message: activity,
    stack: null,
    url: `${window.location.origin}${window.location.pathname}`,
    userAgent: navigator.userAgent,
    source: "kds.activity",
    activity,
    context,
  });
}

export function installClientLogger() {
  ["log", "info", "warn", "error"].forEach((level) => {
    const original = console[level].bind(console);
    console[level] = (...args) => {
      original(...args);
      queueEvent(createEvent(level, args));
    };
  });

  window.addEventListener("error", (event) => {
    queueEvent(
      createEvent(
        "error",
        [event.error || event.message || "Unknown browser error"],
        "window.error"
      )
    );
  });

  window.addEventListener("unhandledrejection", (event) => {
    queueEvent(
      createEvent(
        "error",
        [event.reason || "Unhandled promise rejection"],
        "unhandledrejection"
      )
    );
  });
}
